/* eslint-disable react/require-default-props */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Spin, Table, Tag, Typography } from 'antd'
import clsx from 'clsx'
import { ANNOTATION_CATEGORIES } from 'constants/annotation'
import {
  CommentFormService,
  CommentItem,
  decodeHtmlEntities,
  ObjectCategory
} from 'domain/comment'
import useMutation from 'hooks/useMutation'
import QuillEditorV3, {
  QuillEditorV3Ref
} from 'pages/building/panoramas/components/ModalAlertSetting/QuillEditorV3'
import {
  fetchCommentBodyContent,
  isS3Url,
  uploadCommentBodyToS3
} from 'utils/commentS3Utils'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

export const CATEGORIES = ANNOTATION_CATEGORIES

type Props = {
  open: boolean
  comment?: CommentItem | null
  userList?: any[]
  userGroups?: any[]
  getOrgString?: (u: any) => string
  getRoleString?: (u: any) => string
  onCancel: () => void
  onSave: (payload: any) => void
  onPreCheck?: () => Promise<boolean> | boolean
  onDelete?: (commentId: number) => void
  isSubmitting?: boolean
}

export default function ModalCommentFormRN({
  open,
  comment = null,
  userList = [],
  userGroups = [],
  getOrgString = () => '-',
  getRoleString = () => '-',
  onCancel,
  onSave,
  onPreCheck,
  onDelete,
  isSubmitting = false
}: Props) {
  const { t } = useTranslation()
  const { trigger: getPresignedUrl } = useMutation(
    'v1/annotations/presigned-urls'
  )
  const quillEditorRef = useRef<QuillEditorV3Ref>(null)
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const [isLocalSubmitting, setIsLocalSubmitting] = useState(false)
  const [title, setTitle] = useState('')
  const [objectCat, setObjectCat] = useState<ObjectCategory>('A')
  const [body, setBody] = useState('')
  const [recipients, setRecipients] = useState<number[]>([])
  const [recipientSearch, setRecipientSearch] = useState('')
  const [titleError, setTitleError] = useState(false)
  const [titleLengthError, setTitleLengthError] = useState(false)
  const [showMentionMenu, setShowMentionMenu] = useState(false)
  const [mentionSearchQuery, setMentionSearchQuery] = useState('')
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0)
  const [mentionPos, setMentionPos] = useState<{
    left: number
    top: number
  } | null>(null)
  const [isBodyLoading, setIsBodyLoading] = useState<boolean>(false)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showMentionMenu &&
        editorWrapperRef.current &&
        !editorWrapperRef.current.contains(event.target as Node)
      ) {
        setShowMentionMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMentionMenu])
  const insertMentionTag = useCallback(
    (type: string, name: string, targetId?: number | string) => {
      if (quillEditorRef.current) {
        quillEditorRef.current.insertMention(type, name, targetId)
      } else {
        const tagHtml = CommentFormService.buildMentionTagHtml(
          type,
          name,
          targetId
        )
        setBody((prev) => `${prev || ''}${tagHtml}&nbsp;`)
      }
      setShowMentionMenu(false)
      setMentionSearchQuery('')
    },
    []
  )

  useEffect(() => {
    setMentionSelectedIndex(0)
  }, [mentionSearchQuery, showMentionMenu])

  const mentionItems = useMemo(() => {
    const { showAll, filteredGroups, filteredUsers } =
      CommentFormService.filterMentions(
        mentionSearchQuery,
        userGroups,
        userList
      )

    const items: Array<{
      id: string
      type: string
      label: string
      action: () => void
    }> = []

    if (showAll) {
      items.push({
        id: 'all',
        type: 'all',
        label: 'all （全体通知）',
        action: () => insertMentionTag('all', 'all')
      })
    }

    filteredGroups.forEach((g: any) => {
      const gName = g.group_name || g.groupName || 'グループ'
      items.push({
        id: `group-${g.id}`,
        type: 'group',
        label: `${gName} （グループ）`,
        action: () => insertMentionTag('group', gName, g.id)
      })
    })

    filteredUsers.forEach((u: any) => {
      const uName =
        `${u.last_name || u.lastName || ''} ${
          u.first_name || u.firstName || ''
        }`.trim() ||
        u.name ||
        'ユーザー'
      items.push({
        id: `user-${u.id || u.user_id}`,
        type: 'user',
        label: uName,
        action: () => insertMentionTag('user', uName, u.id || u.user_id)
      })
    })

    return items
  }, [mentionSearchQuery, userGroups, userList, insertMentionTag])

  useEffect(() => {
    if (showMentionMenu && itemRefs.current[mentionSelectedIndex]) {
      itemRefs.current[mentionSelectedIndex]?.scrollIntoView({
        block: 'nearest'
      })
    }
  }, [mentionSelectedIndex, showMentionMenu])

  const handleQuillKeyDown = useCallback(
    (e: KeyboardEvent): boolean => {
      if (!showMentionMenu) return false

      if (e.key === 'Escape') {
        setShowMentionMenu(false)
        return true
      }

      if (mentionItems.length === 0) return false

      if (e.key === 'ArrowDown') {
        setMentionSelectedIndex((prev) => (prev + 1) % mentionItems.length)
        return true
      }

      if (e.key === 'ArrowUp') {
        setMentionSelectedIndex(
          (prev) => (prev - 1 + mentionItems.length) % mentionItems.length
        )
        return true
      }

      if (
        (e.key === 'Enter' || e.key === 'Tab') &&
        !e.isComposing &&
        e.keyCode !== 229
      ) {
        const selectedItem = mentionItems[mentionSelectedIndex]
        if (selectedItem) {
          selectedItem.action()
          return true
        }
      }

      return false
    },
    [showMentionMenu, mentionItems, mentionSelectedIndex]
  )

  useEffect(() => {
    let isMounted = true
    if (open) {
      setTitle(decodeHtmlEntities(comment?.title || ''))
      setObjectCat(comment?.objectCat || 'A')
      setTitleError(false)
      setShowMentionMenu(false)

      const initialBody = comment?.body || ''
      const targetPath =
        (comment as any)?.s3_path || comment?.s3Path || initialBody

      if (targetPath) {
        setIsBodyLoading(true)
        fetchCommentBodyContent(targetPath)
          .then((content) => {
            if (isMounted) {
              if (isS3Url(content)) {
                setBody('')
              } else {
                setBody(content || '')
              }
            }
          })
          .catch(() => {
            if (isMounted) {
              setBody('')
            }
          })
          .finally(() => {
            if (isMounted) {
              setIsBodyLoading(false)
            }
          })
      } else {
        setBody('')
        setIsBodyLoading(false)
      }

      if (comment?.recipients && comment.recipients.length > 0) {
        const parsed = CommentFormService.parseRecipientIds(comment.recipients)
        setRecipients(
          CommentFormService.ensureAdminRecipients(parsed, userList)
        )
      } else if (!comment && userList.length > 0) {
        setRecipients(
          CommentFormService.ensureAdminRecipients(
            userList
              .map((u: any) => Number(u.id || u.user_id))
              .filter((id: number) => !Number.isNaN(id) && id > 0),
            userList
          )
        )
      } else if (userList.length > 0) {
        setRecipients(CommentFormService.getAdminRecipientIds(userList))
      }
    }
    return () => {
      isMounted = false
    }
  }, [open, comment, userList])

  const filteredUsers = CommentFormService.filterUsers(
    userList,
    recipientSearch
  )

  const handleSelectAllRecipients = () => {
    setRecipients(
      userList
        .map((u: any) => Number(u.id || u.user_id))
        .filter((id: number) => !Number.isNaN(id) && id > 0)
    )
  }

  const handleDeselectAllRecipients = () => {
    setRecipients(CommentFormService.getAdminRecipientIds(userList))
  }

  const handleToggleRecipient = (userId: number) => {
    setRecipients((prev) =>
      CommentFormService.toggleRecipient(prev, userId, userList)
    )
  }

  const toggleGroupSelection = (memberIds: number[]) => {
    setRecipients((prev) =>
      CommentFormService.toggleGroupRecipients(prev, memberIds, userList)
    )
  }

  const handleSave = async () => {
    if (isSubmitting || isLocalSubmitting) return

    const currentTitle = title.trim()
    if (!currentTitle) {
      setTitleError(true)
      setTitleLengthError(false)
      return
    }
    if (currentTitle.length > 255) {
      setTitleError(false)
      setTitleLengthError(true)
      return
    }
    setTitleError(false)
    setTitleLengthError(false)

    setIsLocalSubmitting(true)
    try {
      if (onPreCheck) {
        const canProceed = await onPreCheck()
        if (!canProceed) {
          setIsLocalSubmitting(false)
          return
        }
      }

      const { s3Path, s3Url } = await uploadCommentBodyToS3(
        body,
        getPresignedUrl
      )
      const mentions = CommentFormService.extractMentionsFromHtml(body)

      await onSave({
        id: comment?.id,
        title: currentTitle,
        objectCat,
        scope: comment?.scope || 'common',
        status: comment?.status || 'open',
        body: s3Url,
        s3_path: s3Path,
        s3Path,
        recipients,
        mentions
      })
    } catch {
      // ignore
    } finally {
      setIsLocalSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width="1000px"
      rootClassName="shared-permission-modal"
    >
      <div className="flex flex-col w-full">
        {/* Header Title Section (Fixed) */}
        <div className="flex flex-col w-full mb-4">
          <Title
            bottomMargin="mb-0"
            title={
              comment
                ? t('comment.edit_title', 'コメント編集')
                : t('comment.new_title', '新規コメント')
            }
          />
        </div>

        {/* Scrollable Form Content Container (X-scroll disabled) */}
        <div className="max-h-[60vh] overflow-y-auto overflow-x-hidden pr-2 pb-2">
          {/* Title Field */}
          <div className="mb-4">
            <Typography.Text className="mb-1 block text-[12px] font-semibold text-slate-700">
              タイトル <span className="text-rose-500">*</span>
            </Typography.Text>
            <Input
              id="comment-title-input"
              placeholder="コメントのタイトルを入力"
              value={title}
              maxLength={255}
              status={titleError || titleLengthError ? 'error' : ''}
              onChange={(e) => {
                const val = e.target.value
                setTitle(val)
                if (val.trim()) setTitleError(false)
                if (val.trim().length <= 255) setTitleLengthError(false)
              }}
              className="h-10 text-[13px]"
            />
            {titleError && (
              <div className="mt-1 text-[11px] text-rose-500">
                {t('comment.titleRequired', 'タイトルを入力してください')}
              </div>
            )}
            {titleLengthError && (
              <div className="mt-1 text-[11px] text-rose-500">
                {t(
                  'comment.titleMaxLength',
                  'タイトルは255文字以内で入力してください'
                )}
              </div>
            )}
          </div>

          {/* Category Field */}
          <div className="mb-4">
            <Typography.Text className="mb-1 block text-[12px] font-semibold text-slate-700">
              カテゴリ
            </Typography.Text>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => {
                const isSelected = objectCat === cat.key
                return (
                  <Tag
                    key={cat.key}
                    color={isSelected ? cat.colorHex : undefined}
                    className={clsx(
                      'cursor-pointer px-3 py-1 text-[12px] font-semibold rounded-full flex items-center gap-1.5 transition-all select-none',
                      !isSelected &&
                        'border border-solid border-slate-300 text-slate-600 bg-white hover:bg-slate-50'
                    )}
                    onClick={() => setObjectCat(cat.key as ObjectCategory)}
                  >
                    <span
                      className="flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-black"
                      style={{
                        backgroundColor: isSelected
                          ? 'rgba(255, 255, 255, 0.25)'
                          : cat.colorHex,
                        color: '#fff',
                        fontFamily: 'Georgia, serif'
                      }}
                    >
                      {cat.key}
                    </span>
                    {cat.label}
                  </Tag>
                )
              })}
            </div>
          </div>

          {/* Main Body Field */}
          <div className="mb-4">
            <div className="mb-1">
              <Typography.Text className="text-[12px] font-semibold text-slate-700">
                本文
              </Typography.Text>
            </div>
            <div ref={editorWrapperRef} className="relative">
              {isBodyLoading && (
                <div className="flex h-40 items-center justify-center gap-2 rounded bg-slate-50 text-xs text-slate-400">
                  <Spin size="small" />
                  <span>コメント本文を読み込み中...</span>
                </div>
              )}
              <div className={isBodyLoading ? 'hidden' : 'block'}>
                <QuillEditorV3
                  ref={quillEditorRef}
                  value={body}
                  onChange={(content) => {
                    setBody(content)
                  }}
                  onKeyDown={handleQuillKeyDown}
                  onMentionTrigger={(pos) => {
                    if (pos) {
                      setMentionPos({ left: pos.left, top: pos.top })
                      if (pos.query !== undefined) {
                        setMentionSearchQuery(pos.query)
                      }
                    }
                    setShowMentionMenu(true)
                  }}
                  onMentionClose={() => setShowMentionMenu(false)}
                  placeholder="コメント本文を入力（@でメンション、@all で全員、@グループ名 でグループ）"
                  height={180}
                />
              </div>

              {showMentionMenu && (
                <div
                  style={
                    mentionPos
                      ? {
                          top: mentionPos.top,
                          left: Math.min(Math.max(10, mentionPos.left), 220),
                          transform: 'translateY(calc(-100% - 5px))'
                        }
                      : { bottom: '100%', left: 8 }
                  }
                  className="absolute z-50 mb-1 w-64 max-h-56 overflow-y-auto rounded-md bg-white p-2 shadow-lg ring-1 ring-black/5 text-xs"
                >
                  <div className="mb-1 flex items-center justify-between px-1">
                    <span className="text-[11px] font-bold text-slate-500">
                      メンションを選択
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowMentionMenu(false)}
                      className="text-[11px] text-slate-400 hover:text-slate-600 bg-transparent border-0 cursor-pointer"
                    >
                      ✕
                    </button>
                  </div>
                  <Input
                    size="small"
                    value={mentionSearchQuery}
                    onChange={(e) => setMentionSearchQuery(e.target.value)}
                    placeholder="検索..."
                    className="mb-2"
                  />
                  <div className="space-y-1">
                    {mentionItems.map((item, index) => {
                      const isSelected = index === mentionSelectedIndex
                      let colorClasses = 'hover:bg-sky-50 text-sky-700'
                      if (item.type === 'all')
                        colorClasses = 'hover:bg-amber-50 text-amber-700'
                      if (item.type === 'group')
                        colorClasses = 'hover:bg-violet-50 text-violet-700'

                      return (
                        <button
                          key={item.id}
                          ref={(el) => {
                            itemRefs.current[index] = el
                          }}
                          type="button"
                          onClick={item.action}
                          className={clsx(
                            'block w-full rounded px-2 py-1 text-left font-semibold border-0 cursor-pointer text-xs transition-colors',
                            colorClasses,
                            isSelected &&
                              'bg-slate-200 ring-1 ring-slate-400 font-bold'
                          )}
                        >
                          {item.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipients Table Section */}
          <div className="rounded-lg border border-solid border-slate-200 bg-slate-50/60 p-4 mb-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[13px] font-semibold text-slate-700">
                公開先
              </div>
              <button
                type="button"
                className="ml-auto rounded border border-solid border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => {
                  if (
                    recipients.length === userList.length &&
                    userList.length > 0
                  ) {
                    handleDeselectAllRecipients()
                  } else {
                    handleSelectAllRecipients()
                  }
                }}
              >
                {recipients.length === userList.length && userList.length > 0
                  ? '全解除'
                  : '全員を選択'}
              </button>
            </div>

            {userGroups.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-md bg-violet-50/70 p-2 border border-solid border-violet-100">
                <span className="text-[11px] font-bold text-violet-700 mr-1 shrink-0">
                  グループから選択:
                </span>
                {userGroups.map((g: any) => {
                  const memberIds = CommentFormService.getGroupMemberIds(g)
                  const selectedCount = memberIds.filter((mid) =>
                    recipients.includes(mid)
                  ).length
                  const isAllGroupSelected =
                    memberIds.length > 0 && selectedCount === memberIds.length
                  const groupName = g.group_name || g.groupName

                  return (
                    <Tag
                      key={g.id}
                      color={isAllGroupSelected ? 'purple' : undefined}
                      className={clsx(
                        'cursor-pointer px-2.5 py-0.5 text-[11px] font-semibold rounded-full transition-all select-none max-w-full inline-flex items-center gap-1 m-0',
                        !isAllGroupSelected &&
                          'bg-white border-violet-300 text-violet-700 hover:bg-violet-50'
                      )}
                      onClick={() => toggleGroupSelection(memberIds)}
                    >
                      <span
                        className="truncate max-w-[150px]"
                        title={groupName}
                      >
                        {groupName}
                      </span>
                      <span
                        className={clsx(
                          'rounded-full px-1 text-[9px] shrink-0 font-medium',
                          isAllGroupSelected
                            ? 'bg-purple-200/60 text-purple-800'
                            : 'bg-violet-100 text-violet-700'
                        )}
                      >
                        {selectedCount}/{memberIds.length}
                      </span>
                    </Tag>
                  )
                })}
              </div>
            )}

            <div className="mb-3">
              <Input
                placeholder="名前もしくはメールアドレスを入力"
                value={recipientSearch}
                onChange={(e) => setRecipientSearch(e.target.value)}
                allowClear
                prefix={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-slate-400 mr-1"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                }
                className="h-9 text-[13px]"
              />
            </div>

            <Table
              dataSource={filteredUsers}
              rowKey={(u: any) => Number(u.id || u.user_id)}
              pagination={false}
              scroll={{ y: 260 }}
              size="small"
              bordered
              columns={[
                {
                  title: (
                    <input
                      type="checkbox"
                      checked={
                        recipients.length === userList.length &&
                        userList.length > 0
                      }
                      onChange={() => {
                        if (recipients.length === userList.length) {
                          handleDeselectAllRecipients()
                        } else {
                          handleSelectAllRecipients()
                        }
                      }}
                      className="cursor-pointer accent-blue-600"
                      title={
                        recipients.length === userList.length
                          ? '全解除'
                          : '全員を選択'
                      }
                    />
                  ),
                  dataIndex: 'id',
                  width: 40,
                  align: 'center',
                  render: (_, u: any) => {
                    const uid = Number(u.id || u.user_id)
                    const isMandatory =
                      CommentFormService.isAdminRecipientRole(u)
                    const isChecked = isMandatory || recipients.includes(uid)
                    return (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isMandatory}
                        onChange={() => {
                          if (!isMandatory) {
                            handleToggleRecipient(uid)
                          }
                        }}
                        className={
                          isMandatory
                            ? 'cursor-not-allowed opacity-70'
                            : 'cursor-pointer'
                        }
                        title={
                          isMandatory
                            ? '管理者（支店管理者・お客様管理者・システム管理者）は常に公開先に含まれます'
                            : undefined
                        }
                      />
                    )
                  }
                },
                {
                  title: '名前',
                  dataIndex: 'name',
                  render: (_, u: any) => (
                    <span className="font-medium text-slate-800 truncate block max-w-[140px]">
                      {`${u.last_name || u.lastName || ''} ${
                        u.first_name || u.firstName || ''
                      }`.trim() ||
                        u.name ||
                        'ユーザー'}
                    </span>
                  )
                },
                {
                  title: '組織名',
                  dataIndex: 'organization',
                  render: (_, u: any) => (
                    <span className="truncate block max-w-[140px]">
                      {getOrgString(u)}
                    </span>
                  )
                },
                {
                  title: 'メールアドレス',
                  dataIndex: 'email',
                  render: (email: string) => (
                    <span
                      className="truncate block max-w-[180px]"
                      title={email}
                    >
                      {email || '-'}
                    </span>
                  )
                },
                {
                  title: '権限',
                  dataIndex: 'role',
                  width: 100,
                  render: (_, u: any) => <Tag>{getRoleString(u)}</Tag>
                }
              ]}
            />
          </div>
        </div>

        {/* Footer Action Buttons (Fixed) matching ModalAddAnnotation */}
        <div className="flex w-full items-center pt-5 px-2 mt-4 justify-between border-0 border-t border-solid border-[#EEEFF2]">
          {comment && onDelete && (
            <Button
              danger
              type="primary"
              onClick={() => onDelete(comment.id)}
              sizing="w-[140px] h-[50px]"
            >
              {t('common.titleDeleteAnnotation', '削除')}
            </Button>
          )}

          <div className="flex gap-4 ml-auto">
            <Button sizing="w-[140px] h-[50px]" onClick={onCancel}>
              {t('common.cancel', 'キャンセル')}
            </Button>
            <Button
              type="primary"
              sizing="w-[140px] h-[50px]"
              className="theme-bg-primary"
              loading={isSubmitting || isLocalSubmitting}
              disabled={isSubmitting || isLocalSubmitting}
              onClick={handleSave}
            >
              {comment
                ? t('common.save', '保存')
                : t('comment.create', 'コメントを作成')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
